import { defineStore } from "pinia";

import type { Team } from "@/model/session";
import { useGlobalI18n } from "@/translations/i18n";

import { NO_ART, useArtStore } from "./art";
import { useUserStore } from "./user";

export const NO_TEAM = { id: "", name: "" };

interface State {
  teams: Team[];
  externals: Team[];
  current: Team;
}

const { t } = useGlobalI18n();

export const useTeamStore = defineStore("team", {
  state: (): State => ({ teams: [], externals: [], current: NO_TEAM }),
  getters: {
    findTeam(state) {
      return (query: { id?: string; name?: string; withExternal?: boolean }) => {
        if (query.id || query.name) {
          const teams = query.withExternal ? [...state.teams, ...state.externals] : state.teams;
          return teams.find(
            (t) => (!query.id || query.id === t.id) && (!query.name || query.name === t.name),
          );
        }
      };
    },
    teamsInArt(state) {
      return (artId: string, withExternal = false): Team[] => {
        const teams = withExternal ? [...state.teams, ...state.externals] : state.teams;
        // REN-9638 backward compatibility fix
        return artId === "" ? teams : teams.filter((team) => team.artId === artId);
      };
    },
    teamsInCurrentArt(state): Team[] {
      const artId = useArtStore().current.id;
      return artId ? this.teamsInArt(artId) : state.teams;
    },
    noTeam: () => ({ id: NO_TEAM.id, name: t("general.noTeam") }),
    isExternalTeam(state) {
      return (teamId: string): boolean =>
        !!state.externals.find((team: Team) => team.id === teamId);
    },
  },
  actions: {
    setTeams(teams: Team[], externals: Team[]) {
      this.teams = teams;
      this.externals = externals;
      this.setCurrent(NO_TEAM);
    },
    setCurrent(team: Team) {
      this.current = team;
      useArtStore().setCurrent(useArtStore().artById(team.artId) || NO_ART);
    },
  },
});

export function isNotUserTeam(team: Team) {
  const userTeams = useUserStore().technicalUser.teams;
  return !userTeams.some((userTeam) => userTeam.id === team.id);
}
